import React, { useEffect, useState } from 'react';
import {
  AuthenticateMutation,
  AuthenticationMethodType,
  AuthenticationMethodsFragment,
  useAuthenticateMutation,
  useGetAuthenticationMethodsLazyQuery,
  useMfaAccessTokenMutation,
} from '@/generated/graphql';
import { PageLoader } from '@/components/Elements/PageLoader/PageLoader';
import { LoginView } from '@/enums/LoginView';
import { Login } from '@/components/Views/Login/Login/Login';
import { EMAIL, MFA_AUTH_TOKEN, MFA_COOKIE } from '@/constants/authentication';
import { Error } from '@/enums/Error';
import { GraphQLErrorExtensions } from '@/types/GraphQLErrorExtensions';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { useTranslation } from 'react-i18next';
import { getClientDomainType, setAuthTokensToLocalStorage } from '@/utils/auth';
import { ClientType } from '@/generated/graphql';
import { RedirectApp } from '@/enums/RedirectApp';
import { REDIRECT_APP } from '@/constants/authentication';
import { useSearchParams } from 'react-router-dom';
import { routes } from '@/constants/routes';
import { getSecondsLeftFromMessage } from '@/utils/errorMessage';
import { useMounted } from '@/hooks/useMounted';
import { redirectToSSOAuthentication } from '@/helpers/redirectToSSOAuthentication';
import { useRedirect } from '@/hooks/useRedirect';
import { useRedirectAppRouter } from '@/hooks/useRedirectAppRouter';
import { useAuthBranding } from '@/components/Providers/BrandingProvider/BrandingProvider';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// This flag will be used in cased indirect invite accept fails
// after redirect
const SIGNED_IN_QUERY_PARAMS = {
  signedIn: String(true),
};

export default function LoginPage() {
  const router = useRedirectAppRouter();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const redirectFromLocalStorage = useRedirect();
  const { branding } = useAuthBranding();
  useDocumentTitle(branding ? t('login.title') : t('login.pageTitle'));

  //If we pass email as a query param, we need to fetch auth methods right away and show view based on that
  const [loadingAuthMethods, setLoadingAuthMethods] = useState(
    !!searchParams.get('email')
  );
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [view, setView] = useState<LoginView>(LoginView.PROVIDE_EMAIL);
  const [mfaCookie, setMfaCookie] = useState<string>();

  const handleGeneralError = () => {
    setSubmitting(false);
    // A failed auth-methods lookup must not leave the page loader up forever.
    setLoadingAuthMethods(false);
  };

  const [requestMfaAccessToken] = useMfaAccessTokenMutation({
    onCompleted(data) {
      localStorage.setItem(MFA_AUTH_TOKEN, data.mfaAccessToken.mfaAccessToken);
      if (searchParams.get('indirect-inv')) {
        router.pushWithIndirectInvParams(routes.twoFactorAuthentication);
      } else {
        router.pushWithRedirectAppParam(routes.twoFactorAuthentication);
      }
    },
    onError: handleGeneralError,
  });

  const [authenticate] = useAuthenticateMutation({});

  const mounted = useMounted();

  const getClientType = (): ClientType => {
    const redirectApp = localStorage.getItem(REDIRECT_APP) as RedirectApp;
    
    switch (redirectApp) {
      case RedirectApp.ADMIN:
        return ClientType.Admin;
      case RedirectApp.COMPANY:
        return ClientType.Company;
      case RedirectApp.CANDIDATE:
        return ClientType.Candidate;
      default:
        return ClientType.Candidate; // Default fallback
    }
  };

  useEffect(() => {
    if (searchParams.get('view') === LoginView.EMAIL_PASSWORD) {
      setView(LoginView.EMAIL_PASSWORD);
    }

    // `loadingAuthMethods` starts as true whenever an email is in the URL, so
    // the lookup must always run in that case or the page loader never clears.
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }

    setMfaCookie(JSON.parse(localStorage.getItem(MFA_COOKIE) || '[]'));
  }, []);

  const [getAuthenticationMethods, { data }] =
    useGetAuthenticationMethodsLazyQuery({
      onCompleted: data => {
        const methods = data?.GetAuthenticationMethods || [];

        const hasSSO = methods.some(method => method.type === AuthenticationMethodType.Saml)
        const hasEmail = methods.some(method => method.type === AuthenticationMethodType.EmailPassword)
        const hasBoth = hasSSO && hasEmail
        if (hasBoth) {
          setView(LoginView.SSO_EMAIL_PASSWORD)
        } else if (hasEmail) {
          setView(LoginView.EMAIL_PASSWORD)
        } else if (hasSSO) {
          setView(LoginView.SSO)
        }
        
        setLoadingAuthMethods(false);
        setSubmitting(false);
      },
      onError: handleGeneralError,
    });

  const authMethods: AuthenticationMethodsFragment[] =
    data?.GetAuthenticationMethods || [];

  useEffect(() => {
    if (email) {
      getAuthenticationMethods({
        variables: {
          email,
        },
      });
    }
  }, [email]);

  const onEmailSubmit = (email: string) => {
    setSubmitting(true);
    setEmail(email);
  };

  const handleAuthError = (
    error: GraphQLErrorExtensions,
    email: string,
    password: string
  ) => {
    const { code, message } = error?.response;

    switch (code) {
      case Error.INVALID_CREDENTIALS: {
        handleMsgType({
          type: TNotification.error,
          title: t('login.failed'),
          message: `${t('login.yourEmailOrPasswordDoNotMatch')}`,
        });
        break;
      }

      case Error.EXCEEDED_NUMBER_OF_ATTEMPTS: {
        handleMsgType({
          type: TNotification.error,
          title: t('login.failed'),
          message: t('login.dueToMultipleFailedLoginAttempts', {
            secondsLeft: getSecondsLeftFromMessage(message),
          }),
        });
        break;
      }

      case Error.MFA_REQUIRED: {
        requestMfaAccessToken({
          variables: {
            email: email,
            password: password,
            clientDomainType: getClientDomainType(),
          },
        });
        break;
      }
    }
  };

  const saveAuthTokensAndRedirect = (data: AuthenticateMutation) => {
    const { authCode } = data.ClientAuthenticate;
    setAuthTokensToLocalStorage(authCode);

    if (searchParams.get('indirect-inv')) {
      router.pushWithIndirectInvParams(routes.signUp, SIGNED_IN_QUERY_PARAMS);
    } else {
      redirectFromLocalStorage();
    }
  };

  const handleEmailPasswordLogin = (email: string, password: string) => {
    setSubmitting(true);
    authenticate({
      variables: {
        email,
        password,
        mfaCookie,
        clientType: getClientType(),
      },
      onCompleted: data => {
        saveAuthTokensAndRedirect(data);
      },
      onError: ({ graphQLErrors }) => {
        graphQLErrors?.forEach(({ extensions }) => {
          setSubmitting(false);
          if (extensions) {
            handleAuthError(
              extensions?.exception as GraphQLErrorExtensions,
              email,
              password
            );
          }
        });
      },
    });
  };

  const handleSSOLogin = async () => {
    const subId = authMethods.find(
      method => method.type === AuthenticationMethodType.Saml
    )?.authInfoSubId;

    if (subId) {
      //Save email to localStorage to enable to return to this screen if sso sign-in fails
      localStorage.setItem(EMAIL, email);
      await redirectToSSOAuthentication(subId);
    }
  };

  const restartSigninProcess = () => {
    setEmail('');
    setView(LoginView.PROVIDE_EMAIL);
    localStorage.removeItem(EMAIL);
  };

  return (
    <section>
      {!mounted || loadingAuthMethods ? (
        <PageLoader />
      ) : (
        <Login
          view={view}
          email={email}
          submitting={submitting}
          onEmailSubmit={onEmailSubmit}
          handleEmailPasswordLogin={handleEmailPasswordLogin}
          handleSSOLogin={handleSSOLogin}
          restartSigninProcess={restartSigninProcess}
        />
      )}
    </section>
  );
}
